/**
 * Plausibilitätsprüfung für Eingangsrechnungen.
 *
 * Reine Funktion: Server (nach Auslese und jeder Änderung) und Client (Anzeige) rechnen dasselbe.
 * Fehler sperren die Freigabe, Warnungen müssen einzeln bestätigt werden.
 */
import { normalizeIban } from '../../../utils/iban';

export type InvoiceCheckSeverity = 'error' | 'warning';

export const INVOICE_CHECK_CODES = [
  'missing_supplier',
  'missing_invoice_number',
  'missing_issue_date',
  'missing_amount',
  'duplicate',
  'sum_mismatch',
  'tax_lines_sum_mismatch',
  'tax_rate_mismatch',
  'invalid_tax_rate',
  'reverse_charge_with_tax',
  'einvoice_invalid',
  'einvoice_rule_violation',
  'exchange_rate_missing',
  'iban_differs',
  'supplier_unlinked',
  'supplier_vat_id_missing',
  'supplier_vat_id_invalid',
  'supplier_vat_id_differs',
  'recipient_mismatch',
  'recipient_vat_id_differs',
  'issue_date_future',
  'issue_date_old',
  'due_before_issue',
  'no_allocation',
  'small_business_with_tax',
  'tax_rate_unverified',
  'einvoice_missing',
  'pdf_xml_mismatch',
  'exchange_rate_deviation',
  'amount_outlier',
] as const;

export type InvoiceCheckCode = (typeof INVOICE_CHECK_CODES)[number];

export type InvoiceCheck = {
  code: InvoiceCheckCode;
  severity: InvoiceCheckSeverity;
  /** Feld im Detail-Modal, das markiert wird. */
  field: string | null;
  message: string;
  /** Besonders hervorheben (z. B. abweichende IBAN: Betrugsschutz). */
  highlight?: boolean;
};

export type InvoiceTaxLine = {
  /** Steuersatz in Prozent, z. B. 19. */
  rate: number;
  net: number;
  tax: number;
  /** EN-16931-Kategorie (S, Z, E, AE, K, …), falls bekannt. */
  category?: string | null;
};

export type IncomingInvoiceCheckInput = {
  today: string;
  isCredit: boolean;
  organizationId: string | null;
  partyName: string | null;
  invoiceNumber: string | null;
  issueDate: string | null;
  dueDate: string | null;
  netAmount: number | null;
  taxAmount: number | null;
  grossAmount: number | null;
  currency: string | null;
  taxLines: InvoiceTaxLine[] | null;
  /** Einheiten Fremdwährung je 1 EUR. */
  exchangeRate: number | null;
  exchangeRateSource: 'ecb' | 'manual' | null;
  /** EZB-Kurs zum Rechnungsdatum, für den Vergleich mit einem manuellen Kurs. */
  ecbRate: number | null;
  payeeIban: string | null;
  supplierVatId: string | null;
  supplierTaxNumber: string | null;
  recipientName: string | null;
  recipientVatId: string | null;
  reverseChargeHint: boolean;
  smallBusinessHint: boolean;
  leadId: string | null;
  isOverhead: boolean;
  einvoiceStatus: 'valid' | 'invalid' | 'none' | null;
  einvoiceErrors: string[];
  /**
   * true: XML unlesbar oder Profil ohne Rechnungscharakter (sperrt die Freigabe).
   * false: Regelverstöße laut Validierung (Hinweis; unsere Prüfung ist keine offizielle KoSIT-Validierung).
   */
  einvoiceBlocking: boolean;
  /** Felder, in denen der PDF-Text vom XML abweicht (ZUGFeRD). */
  pdfXmlMismatchFields: string[];
  isDuplicate: boolean;
  supplier: { iban: string | null; vatId: string | null } | null;
  ownCompany: { name: string | null; vatId: string | null } | null;
  /** Bruttobeträge früherer Rechnungen desselben Lieferanten (EUR). */
  supplierHistoryGross: number[];
};

/** Kleinbetragsrechnung (§ 33 UStDV): keine USt-IdNr./Steuernummer und keine E-Rechnung nötig. */
export const SMALL_INVOICE_LIMIT_EUR = 250;

/** Ab diesem Rechnungsdatum müssen B2B-Lieferanten mit > 800.000 € Vorjahresumsatz E-Rechnungen stellen. */
export const SUPPLIER_EINVOICE_FIRST_STAGE = '2027-01-01';
/** Ab diesem Rechnungsdatum gilt die E-Rechnungspflicht für alle B2B-Lieferanten. */
export const SUPPLIER_EINVOICE_ALL = '2028-01-01';

const AMOUNT_TOLERANCE = 0.02;
const RATE_TOLERANCE_PP = 0.6;
const MANUAL_RATE_MAX_DEVIATION = 0.05;
const OUTLIER_FACTOR = 3;
const OUTLIER_MIN_ABS = 200;
const OUTLIER_MIN_HISTORY = 3;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function isNum(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function abs(value: number | null): number | null {
  return isNum(value) ? Math.abs(value) : null;
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatEur(n: number): string {
  return `${round2(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function formatRate(rate: number): string {
  return `${rate.toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`;
}

/** In Deutschland zulässige Steuersätze zum Rechnungsdatum (inkl. Senkung 2. Hj. 2020). */
export function allowedGermanVatRates(issueDate: string | null): number[] {
  const date = String(issueDate ?? '').slice(0, 10);
  if (date >= '2020-07-01' && date <= '2020-12-31') return [0, 5, 7, 16, 19];
  return [0, 7, 19];
}

export function normalizeVatId(value: string | null | undefined): string {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** Grobe Formatprüfung der USt-IdNr. (DE exakt, übrige EU-Länder Länge/Zeichen). */
export function isPlausibleVatId(value: string | null | undefined): boolean {
  const id = normalizeVatId(value);
  if (!id) return false;
  if (id.startsWith('DE')) return /^DE\d{9}$/.test(id);
  if (id.startsWith('AT')) return /^ATU\d{8}$/.test(id);
  return /^[A-Z]{2}[A-Z0-9]{2,13}$/.test(id);
}

function normalizeCompanyName(value: string | null | undefined): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/\b(gmbh|mbh|ug|haftungsbeschränkt|kg|ohg|gbr|ag|e\.?\s?k\.?|inh\.?|co\.?)\b/g, ' ')
    .replace(/[^a-z0-9äöüß]+/g, ' ')
    .trim();
}

function companyNamesMatch(a: string | null, b: string | null): boolean {
  const x = normalizeCompanyName(a);
  const y = normalizeCompanyName(b);
  if (!x || !y) return true;
  return x === y || x.includes(y) || y.includes(x);
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Bruttobetrag in EUR (Fremdwährung über den Kurs). null, wenn kein Kurs vorliegt. */
export function grossAmountEur(input: Pick<IncomingInvoiceCheckInput, 'grossAmount' | 'currency' | 'exchangeRate'>): number | null {
  if (!isNum(input.grossAmount)) return null;
  const currency = String(input.currency ?? 'EUR').toUpperCase() || 'EUR';
  if (currency === 'EUR') return input.grossAmount;
  if (!isNum(input.exchangeRate) || input.exchangeRate <= 0) return null;
  return round2(input.grossAmount / input.exchangeRate);
}

export function runIncomingInvoiceChecks(input: IncomingInvoiceCheckInput): InvoiceCheck[] {
  const checks: InvoiceCheck[] = [];
  const add = (
    code: InvoiceCheckCode,
    severity: InvoiceCheckSeverity,
    field: string | null,
    message: string,
    highlight?: boolean,
  ) => {
    checks.push({ code, severity, field, message, ...(highlight ? { highlight: true } : {}) });
  };

  const net = abs(input.netAmount);
  const tax = abs(input.taxAmount);
  const gross = abs(input.grossAmount);
  const currency = String(input.currency ?? 'EUR').trim().toUpperCase() || 'EUR';
  const issueDate = String(input.issueDate ?? '').slice(0, 10) || null;
  const grossEur = abs(grossAmountEur(input));

  // --- Pflichtangaben ---
  if (!input.organizationId && !String(input.partyName ?? '').trim()) {
    add('missing_supplier', 'error', 'organization_id', 'Kein Lieferant erkannt. Bitte Lieferant zuordnen.');
  } else if (!input.organizationId) {
    add(
      'supplier_unlinked',
      'warning',
      'organization_id',
      'Lieferant ist keinem Kontakt zugeordnet. Ohne Zuordnung fehlen Stammdaten (IBAN, USt-IdNr.) für den Abgleich.',
    );
  }
  if (!String(input.invoiceNumber ?? '').trim()) {
    add('missing_invoice_number', 'error', 'invoice_number', 'Rechnungsnummer fehlt.');
  }
  if (!issueDate) {
    add('missing_issue_date', 'error', 'issue_date', 'Rechnungsdatum fehlt.');
  }
  if (gross == null || gross === 0) {
    add('missing_amount', 'error', 'gross_amount', 'Bruttobetrag fehlt.');
  }
  if (input.isDuplicate) {
    add('duplicate', 'error', 'invoice_number', 'Möglicherweise doppelt erfasst. Bitte mit dem bestehenden Beleg abgleichen.');
  }

  // --- Rechnerische Prüfung ---
  if (net != null && tax != null && gross != null && Math.abs(net + tax - gross) > AMOUNT_TOLERANCE) {
    add(
      'sum_mismatch',
      'error',
      'gross_amount',
      `Netto ${formatEur(net)} + Steuer ${formatEur(tax)} ergibt nicht den Bruttobetrag ${formatEur(gross)}.`,
    );
  }

  const lines = (input.taxLines ?? []).filter((line) => isNum(line.rate) && isNum(line.net) && isNum(line.tax));
  const allowedRates = allowedGermanVatRates(issueDate);
  if (lines.length > 0) {
    const sumNet = lines.reduce((acc, line) => acc + Math.abs(line.net), 0);
    const sumTax = lines.reduce((acc, line) => acc + Math.abs(line.tax), 0);
    const tolerance = AMOUNT_TOLERANCE * lines.length;
    if ((net != null && Math.abs(sumNet - net) > tolerance) || (tax != null && Math.abs(sumTax - tax) > tolerance)) {
      add(
        'tax_lines_sum_mismatch',
        'error',
        'tax_amount',
        'Die Steueraufstellung nach Sätzen passt nicht zu den Gesamtbeträgen.',
      );
    }
    for (const line of lines) {
      const expected = round2((Math.abs(line.net) * line.rate) / 100);
      const lineTolerance = Math.max(AMOUNT_TOLERANCE, Math.abs(line.net) * 0.0005);
      if (Math.abs(expected - Math.abs(line.tax)) > lineTolerance) {
        add(
          'tax_rate_mismatch',
          'error',
          'tax_amount',
          `Steuer ${formatEur(Math.abs(line.tax))} passt nicht zu ${formatRate(line.rate)} auf ${formatEur(Math.abs(line.net))} (erwartet ${formatEur(expected)}).`,
        );
      }
      if (!allowedRates.includes(line.rate)) {
        add(
          'invalid_tax_rate',
          'warning',
          'tax_amount',
          `Steuersatz ${formatRate(line.rate)} ist in Deutschland nicht gültig. Ausländische Umsatzsteuer ist nicht als Vorsteuer abziehbar.`,
        );
      }
    }
  } else if (net != null && tax != null && net > 0 && tax > 0) {
    const effective = (tax / net) * 100;
    const nearest = allowedRates.filter((rate) => rate > 0).some((rate) => Math.abs(rate - effective) <= RATE_TOLERANCE_PP);
    if (!nearest) {
      add(
        'tax_rate_unverified',
        'warning',
        'tax_amount',
        `Steueranteil entspricht ${formatRate(round2(effective))}. Bei gemischten Steuersätzen bitte die Aufteilung prüfen.`,
      );
    }
  }

  if (input.reverseChargeHint && tax != null && tax > AMOUNT_TOLERANCE) {
    add(
      'reverse_charge_with_tax',
      'error',
      'tax_amount',
      'Die Rechnung verweist auf § 13b UStG (Steuerschuldnerschaft des Leistungsempfängers), weist aber Umsatzsteuer aus.',
    );
  }
  if (input.smallBusinessHint && tax != null && tax > AMOUNT_TOLERANCE) {
    add(
      'small_business_with_tax',
      'warning',
      'tax_amount',
      'Die Rechnung verweist auf die Kleinunternehmerregelung (§ 19 UStG), weist aber Umsatzsteuer aus.',
    );
  }

  // --- E-Rechnung ---
  if (input.einvoiceStatus === 'invalid') {
    const detail = input.einvoiceErrors.slice(0, 2).join('; ');
    if (input.einvoiceBlocking) {
      add(
        'einvoice_invalid',
        'error',
        null,
        `Die E-Rechnung ist nicht verwertbar${detail ? `: ${detail}` : ''}. Bitte beim Lieferanten eine korrigierte Rechnung anfordern.`,
      );
    } else {
      add(
        'einvoice_rule_violation',
        'warning',
        null,
        `Die E-Rechnung verletzt möglicherweise Prüfregeln${detail ? ` (${detail})` : ''}. Bitte prüfen und ggf. eine korrigierte Rechnung anfordern.`,
      );
    }
  }
  if (
    input.einvoiceStatus === 'none'
    && input.organizationId
    && issueDate
    && issueDate >= SUPPLIER_EINVOICE_FIRST_STAGE
    && (grossEur == null || grossEur > SMALL_INVOICE_LIMIT_EUR)
  ) {
    add(
      'einvoice_missing',
      'warning',
      null,
      issueDate >= SUPPLIER_EINVOICE_ALL
        ? 'Keine E-Rechnung (nur PDF/Bild). Seit 2028 müssen Lieferanten E-Rechnungen stellen, sonst ist der Vorsteuerabzug gefährdet.'
        : 'Keine E-Rechnung (nur PDF/Bild). Lieferanten mit mehr als 800.000 € Vorjahresumsatz müssen seit 2027 E-Rechnungen stellen.',
    );
  }
  if (input.pdfXmlMismatchFields.length > 0) {
    add(
      'pdf_xml_mismatch',
      'warning',
      input.pdfXmlMismatchFields[0] ?? null,
      `PDF und eingebettete E-Rechnung weichen ab (${input.pdfXmlMismatchFields.join(', ')}). Maßgeblich sind die XML-Daten.`,
    );
  }

  // --- Fremdwährung ---
  if (currency !== 'EUR') {
    if (!isNum(input.exchangeRate) || input.exchangeRate <= 0) {
      add('exchange_rate_missing', 'error', 'exchange_rate', `Rechnung in ${currency}: Umrechnungskurs zum Rechnungsdatum fehlt.`);
    } else if (
      input.exchangeRateSource === 'manual'
      && isNum(input.ecbRate)
      && input.ecbRate > 0
      && Math.abs(input.exchangeRate - input.ecbRate) / input.ecbRate > MANUAL_RATE_MAX_DEVIATION
    ) {
      add(
        'exchange_rate_deviation',
        'warning',
        'exchange_rate',
        `Der eingetragene Kurs weicht um mehr als 5 % vom EZB-Referenzkurs (${input.ecbRate}) ab.`,
      );
    }
  }

  // --- Abgleich mit Stammdaten ---
  const payeeIban = normalizeIban(input.payeeIban);
  const supplierIban = normalizeIban(input.supplier?.iban);
  if (payeeIban && supplierIban && payeeIban !== supplierIban) {
    add(
      'iban_differs',
      'warning',
      'payee_iban',
      `Die IBAN auf der Rechnung (${payeeIban}) weicht von der hinterlegten IBAN des Lieferanten (${supplierIban}) ab. Vor der Zahlung telefonisch beim Lieferanten bestätigen lassen.`,
      true,
    );
  }

  const invoiceVatId = normalizeVatId(input.supplierVatId);
  const masterVatId = normalizeVatId(input.supplier?.vatId);
  const smallInvoice = grossEur != null && grossEur <= SMALL_INVOICE_LIMIT_EUR;
  if (!invoiceVatId && !String(input.supplierTaxNumber ?? '').trim() && !smallInvoice && !input.smallBusinessHint) {
    add(
      'supplier_vat_id_missing',
      'warning',
      'supplier_vat_id',
      'Weder USt-IdNr. noch Steuernummer des Lieferanten auf der Rechnung (Pflichtangabe nach § 14 Abs. 4 UStG).',
    );
  } else if (invoiceVatId && !isPlausibleVatId(invoiceVatId)) {
    add('supplier_vat_id_invalid', 'warning', 'supplier_vat_id', `USt-IdNr. ${invoiceVatId} hat kein gültiges Format.`);
  } else if (invoiceVatId && masterVatId && invoiceVatId !== masterVatId) {
    add(
      'supplier_vat_id_differs',
      'warning',
      'supplier_vat_id',
      `USt-IdNr. auf der Rechnung (${invoiceVatId}) weicht von den Stammdaten (${masterVatId}) ab.`,
    );
  }

  const ownVatId = normalizeVatId(input.ownCompany?.vatId);
  const recipientVatId = normalizeVatId(input.recipientVatId);
  const recipientName = String(input.recipientName ?? '').trim();
  if (recipientName && input.ownCompany?.name && !companyNamesMatch(recipientName, input.ownCompany.name)) {
    add('recipient_mismatch', 'warning', null, `Die Rechnung ist an „${recipientName}“ adressiert, nicht an das eigene Unternehmen.`);
  } else if (recipientVatId && ownVatId && recipientVatId !== ownVatId) {
    // Name passt (oder fehlt): meist eine veraltete USt-IdNr. im Kundenstamm des Lieferanten.
    add(
      'recipient_vat_id_differs',
      'warning',
      null,
      `Die USt-IdNr. des Empfängers auf der Rechnung (${recipientVatId}) weicht von der eigenen USt-IdNr. (${ownVatId}) ab.`,
    );
  }

  // --- Datum ---
  if (issueDate && issueDate > input.today) {
    add('issue_date_future', 'warning', 'issue_date', 'Rechnungsdatum liegt in der Zukunft.');
  }
  if (issueDate && issueDate < addDaysIso(input.today, -365)) {
    add('issue_date_old', 'warning', 'issue_date', 'Rechnungsdatum liegt mehr als 12 Monate zurück.');
  }
  const dueDate = String(input.dueDate ?? '').slice(0, 10);
  if (issueDate && dueDate && dueDate < issueDate) {
    add('due_before_issue', 'warning', 'due_date', 'Fälligkeit liegt vor dem Rechnungsdatum.');
  }

  // --- Zuordnung und Auffälligkeiten ---
  if (!input.leadId && !input.isOverhead) {
    add('no_allocation', 'warning', 'lead_id', 'Weder Projekt noch Gemeinkosten zugeordnet.');
  }
  const history = input.supplierHistoryGross.map(Math.abs).filter((n) => n > 0);
  if (!input.isCredit && grossEur != null && history.length >= OUTLIER_MIN_HISTORY) {
    const typical = median(history);
    if (grossEur > typical * OUTLIER_FACTOR && grossEur - typical > OUTLIER_MIN_ABS) {
      add(
        'amount_outlier',
        'warning',
        'gross_amount',
        `Betrag ist deutlich höher als üblich bei diesem Lieferanten (typisch ${formatEur(typical)}).`,
      );
    }
  }

  return checks;
}

export function hasBlockingChecks(checks: InvoiceCheck[]): boolean {
  return checks.some((check) => check.severity === 'error');
}

/** message: Wortlaut zum Zeitpunkt der Bestätigung. Ändert sich der Befund (z. B. andere IBAN), gilt sie nicht mehr. */
export type CheckAcknowledgement = { by: string; at: string; note?: string | null; message?: string | null };
export type CheckAcknowledgements = Partial<Record<InvoiceCheckCode, CheckAcknowledgement>>;

export function unacknowledgedWarnings(checks: InvoiceCheck[], acknowledged: CheckAcknowledgements | null | undefined): InvoiceCheck[] {
  const acks = acknowledged ?? {};
  return checks.filter((check) => {
    if (check.severity !== 'warning') return false;
    const ack = acks[check.code];
    return !ack || (Boolean(ack.message) && ack.message !== check.message);
  });
}

/** Liest gespeicherte Prüfergebnisse defensiv (jsonb aus der DB). */
export function parseInvoiceChecks(value: unknown): InvoiceCheck[] {
  if (!Array.isArray(value)) return [];
  const known = new Set<string>(INVOICE_CHECK_CODES);
  return value.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const row = raw as Record<string, unknown>;
    const code = String(row.code ?? '');
    const severity = row.severity === 'error' ? 'error' : row.severity === 'warning' ? 'warning' : null;
    if (!known.has(code) || !severity) return [];
    return [{
      code: code as InvoiceCheckCode,
      severity,
      field: typeof row.field === 'string' ? row.field : null,
      message: String(row.message ?? ''),
      ...(row.highlight === true ? { highlight: true } : {}),
    }];
  });
}

export function parseCheckAcknowledgements(value: unknown): CheckAcknowledgements {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const known = new Set<string>(INVOICE_CHECK_CODES);
  const out: CheckAcknowledgements = {};
  for (const [code, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!known.has(code) || !raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    out[code as InvoiceCheckCode] = {
      by: String(row.by ?? ''),
      at: String(row.at ?? ''),
      note: typeof row.note === 'string' ? row.note : null,
      message: typeof row.message === 'string' ? row.message : null,
    };
  }
  return out;
}

export function parseTaxLines(value: unknown): InvoiceTaxLine[] | null {
  if (!Array.isArray(value)) return null;
  const lines = value.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const row = raw as Record<string, unknown>;
    const rate = Number(row.rate);
    const net = Number(row.net);
    const tax = Number(row.tax);
    if (![rate, net, tax].every(Number.isFinite)) return [];
    return [{ rate, net, tax, category: typeof row.category === 'string' ? row.category : null }];
  });
  return lines.length > 0 ? lines : null;
}
